import { useEffect, useMemo, useState } from 'react'
import {
  Activity, FileText, Globe, Settings as SettingsIcon, Cog,
  Calendar, Sparkles, Layers, Network, Search, Zap, Shield,
  CircleDot, RefreshCw, AlertTriangle, Eye,
} from 'lucide-react'
import { Settings } from '../api'
import {
  BOOSTER_SECTIONS,
  BOOSTER_RESOURCES,
  isBoosterFieldVisible,
  type BoosterSectionName,
} from '../resources_booster'
import {
  SECTIONS as PROFILER_SECTIONS,
  RESOURCES as PROFILER_RESOURCES,
  isFieldVisible as isProfilerFieldVisible,
} from '../resources'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface Props {
  cfg: Settings
  running: boolean
  mode?: string
  campaignId?: string
}

// Map common section names → Lucide icons. Falls back to Cog.
const SECTION_ICONS: Record<string, any> = {
  'Запуск': Zap,
  'Идентификаторы': Shield,
  'Источники данных': FileText,
  'Фильтрация профилей': Layers,
  'Контроль использований': Activity,
  'Расширенные': Sparkles,
  'Прокси': Network,
  'Сайт': Globe,
  'Регион и LR': Globe,
  'Расписание': Calendar,
  'Поиск': Search,
  'Поведение': Eye,
  'Конфигурация': Cog,
}

function pickSectionIcon(section: string) {
  for (const [key, Icon] of Object.entries(SECTION_ICONS)) {
    if (section.toLowerCase().includes(key.toLowerCase())) return Icon
  }
  return Cog
}

function formatValue(key: string, def: any, cfg: Settings): string {
  if (def.type === 'RandomInteger') {
    const min = cfg[`${key}_MIN`] ?? def.minValue ?? ''
    const max = cfg[`${key}_MAX`] ?? def.maxValue ?? ''
    return `${min} — ${max}`
  }
  const v = cfg[key]
  if (v == null || v === '') return '—'
  if (def.type === 'LinesFromFile') return `${String(v).split('\n').filter(Boolean).length} строк`
  return String(v)
}

function formatStartedAt(ts: number): string {
  if (!ts) return ''
  const date = new Date(ts * 1000)
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export default function ConfigSummary({ cfg, running, mode = 'booster', campaignId }: Props) {
  const isBooster = mode === 'booster'
  // For non-booster modes (profiler) we render the profiler resource set
  // — was an empty placeholder before, leaving the bottom Configuration
  // tab blank for profiler users.
  const resources: any = isBooster ? BOOSTER_RESOURCES : PROFILER_RESOURCES
  const sections: readonly string[] = isBooster
    ? BOOSTER_SECTIONS
    : PROFILER_SECTIONS
  const isVisible: (key: string, cfg: Settings) => boolean = isBooster
    ? isBoosterFieldVisible
    : isProfilerFieldVisible

  // When the campaign is running we fetch the *actual* settings snapshot
  // that workers were started with, not the (possibly mid-edit) local
  // form state. When it's not running we fall back to the local cfg.
  const [activeCfg, setActiveCfg] = useState<Settings | null>(null)
  const [activeStartedAt, setActiveStartedAt] = useState<number>(0)
  const [remoteSettings, setRemoteSettings] = useState<Record<string, any> | null>(null)
  const [isStale, setIsStale] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    if (!running || !campaignId) {
      setActiveCfg(null)
      setActiveStartedAt(0)
      setRemoteSettings(null)
      setIsStale(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    // Profiler и booster имеют РАЗНЫЕ active-config эндпоинты — раньше здесь
    // всегда дёргался booster-путь (баг), из-за чего для профайлера снимок и
    // удалённые настройки не приходили.
    const activeUrl = isBooster
      ? `/api/booster/campaigns/${campaignId}/active-config`
      : `/api/profiler/campaigns/${campaignId}/active-config`
    fetch(activeUrl)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d) return
        setActiveCfg(d.settings || {})
        setActiveStartedAt(Number(d.started_at) || 0)
        setRemoteSettings(d.remote_settings && typeof d.remote_settings === 'object' ? d.remote_settings : null)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [running, campaignId, refreshTick, isBooster])

  // Detect "stale local edits" — local cfg differs from running snapshot.
  useEffect(() => {
    if (!activeCfg) { setIsStale(false); return }
    let differs = false
    for (const k of Object.keys(activeCfg)) {
      if (k.startsWith('_')) continue
      if (cfg[k] !== activeCfg[k]) { differs = true; break }
    }
    if (!differs) {
      for (const k of Object.keys(cfg)) {
        if (k.startsWith('_')) continue
        if (cfg[k] && cfg[k] !== activeCfg[k]) { differs = true; break }
      }
    }
    setIsStale(differs)
  }, [activeCfg, cfg])

  // Pick which cfg to render. While running we always want the *active*
  // one; when not running, use the local cfg (the editor state).
  const displayCfg: Settings = running && activeCfg ? activeCfg : cfg

  const visibleEntries = useMemo(
    () => Object.entries(resources).filter(([k]) => isVisible(k, displayCfg)),
    [resources, isVisible, displayCfg],
  )

  const sectionGroups = useMemo(() => sections
    .map(sec => ({
      section: sec as BoosterSectionName,
      fields: visibleEntries.filter(([, def]) => (def as any).section === sec),
    }))
    .filter(g => g.fields.length > 0),
    [sections, visibleEntries],
  )

  const filledCount = visibleEntries.filter(([k]) => {
    const v = displayCfg[k]
    return v != null && v !== ''
  }).length

  if (Object.keys(displayCfg).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-sm text-muted-foreground gap-3">
        <Cog className="h-10 w-10 opacity-40" />
        <span>Конфигурация не загружена</span>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl px-6 py-4 space-y-4">
        {/* Header card with status */}
        <div className="rounded-xl border bg-gradient-to-br from-card to-card/40 px-5 py-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`relative flex h-9 w-9 items-center justify-center rounded-full ${running ? 'bg-emerald-500/10' : 'bg-muted'}`}>
                <CircleDot className={`h-4 w-4 ${running ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                {running && <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping" />}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">
                  {running ? 'Активная конфигурация' : 'Текущие настройки (черновик)'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {running
                    ? (activeStartedAt
                        ? `Воркеры запущены: ${formatStartedAt(activeStartedAt)}`
                        : 'Снимок загружается…')
                    : 'Кампания не запущена — отображается локальный черновик'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono tabular-nums text-[11px]">
                {filledCount} / {visibleEntries.length}
              </Badge>
              {running && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 gap-1 text-xs"
                  onClick={() => setRefreshTick(t => t + 1)}
                  disabled={isLoading}
                  title="Обновить снимок"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                  Обновить
                </Button>
              )}
            </div>
          </div>
          {running && isStale && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-[1px] text-amber-400" />
              <span>
                У вас есть локальные изменения, отличающиеся от запущенной версии. Чтобы применить —
                остановите кампанию, сохраните и запустите заново.
              </span>
            </div>
          )}
        </div>

        {/* Удалённые настройки, фактически применённые при запуске (settings.php).
            Показываем только для профайлера в удалённом режиме, когда снимок получен. */}
        {running && remoteSettings && Object.keys(remoteSettings).length > 0 && (
          <Card className="overflow-hidden border-primary/30">
            <CardHeader className="px-5 py-3 pb-2 border-b border-border/50 bg-primary/5">
              <div className="flex items-center gap-2">
                <Network className="h-3.5 w-3.5 text-primary" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/80">
                  Удалённые настройки (получено при запуске)
                </h3>
                <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
                  {Object.keys(remoteSettings).length}
                </span>
              </div>
            </CardHeader>
            <CardContent className="px-5 py-1">
              <div className="divide-y divide-border/40">
                {Object.entries(remoteSettings).map(([key, val]) => {
                  const disp = val == null || val === '' ? '—' : String(val)
                  const isEmpty = disp === '—'
                  return (
                    <div key={key} className="flex items-center justify-between py-2 gap-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <SettingsIcon className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">{key}</span>
                      </div>
                      <span className={`text-sm text-right font-mono tabular-nums max-w-[55%] break-all ${isEmpty ? 'text-muted-foreground/40' : 'text-foreground font-medium'}`}>{disp}</span>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sections */}
        {sectionGroups.map(g => {
          const Icon = pickSectionIcon(g.section)
          return (
            <Card key={g.section} className="overflow-hidden">
              <CardHeader className="px-5 py-3 pb-2 border-b border-border/50 bg-muted/20">
                <div className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-primary" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/80">{g.section}</h3>
                  <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
                    {g.fields.length}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-5 py-1">
                <div className="divide-y divide-border/40">
                  {g.fields.map(([key, def]) => {
                    const val = formatValue(key, def, displayCfg)
                    const isEmpty = val === '—'
                    return (
                      <div key={key} className="flex items-center justify-between py-2 gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <SettingsIcon className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                          <span className="text-sm text-muted-foreground truncate">{def.description}</span>
                        </div>
                        <span className={`text-sm text-right font-mono tabular-nums max-w-[55%] break-all ${isEmpty ? 'text-muted-foreground/40' : 'text-foreground font-medium'}`}>{val}</span>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
